if (!customElements.get('pet-portrait-form-v3')) {
  customElements.define(
    'pet-portrait-form-v3',
    class PetPortraitFormV3 extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form[data-type="add-to-cart-form"]');
        if (!this.form) return;

        this.sectionId = this.dataset.sectionId || this.form.dataset.sectionId;
        this.submitLabel = this.dataset.submitLabel || '';

        this.wizard = this.querySelector('[data-wizard]');
        this.steps = this.wizard
          ? Array.from(this.wizard.querySelectorAll('[data-wizard-step]'))
          : [];
        this.currentStep = 0;
        this.stepCount = this.steps.length;

        if (window.Shopify && Shopify.designMode) {
          this.steps.forEach((s) => s.classList.add('is-active'));
          const btn = this.form.querySelector('.product-form__submit');
          if (btn) btn.style.display = '';
          return;
        }

        this.form.addEventListener('submit', this.validateFinal.bind(this));

        this.petNameToggle = this.form.querySelector('#pet-name-toggle');
        this.petNameFields = this.form.querySelector('[data-pet-name-fields]');

        if (this.petNameToggle && this.petNameFields) {
          this.petNameToggle.addEventListener('change', () => this.togglePetName());
        }

        this.bindWizardNav();
        this.bindLiveErrorClear();
        this.initStepper();
        this.initStickyCart();
        this.initGuideModal();
        this.initOrderSummary();
        this.initCharCounter();
        this.updateSizePrices();
        this.syncStepVisibility();

        this.bindSizeTracking();
        this.bindSizeGuideLink();
        this.bindFinishTracking();
        this.bindBackgroundTracking();
        this.bindUploadTracking();
        this.subscribeToCartSuccess();
        this.subscribeToVariantChange();
        this.trackEvent('pnh_wizard_start', {
          step_number: 1,
          step_name: 'size'
        });
      }

      /* ── Wizard navigation ── */

      bindWizardNav() {
        this.wizard?.addEventListener('click', (e) => {
          const nextBtn = e.target.closest('[data-wizard-next]');
          const backBtn = e.target.closest('[data-wizard-back]');

          if (nextBtn) {
            e.preventDefault();
            this.nextStep();
          } else if (backBtn) {
            e.preventDefault();
            this.prevStep();
          }
        });
      }

      bindLiveErrorClear() {
        this.wizard?.addEventListener('change', (e) => {
          const input = e.target;
          if (input.type === 'radio') {
            const fieldset = input.closest('fieldset');
            let err = fieldset?.querySelector('.pet-portrait-form__error');
            if (!err) {
              const step = input.closest('[data-wizard-step]');
              err = step?.querySelector('.pet-portrait-form__error');
            }
            if (err) err.hidden = true;
          }
          if (input.type === 'checkbox' || input.tagName === 'SELECT') {
            const group = input.closest('.pet-portrait-form__field, .pet-portrait-form__pet-name-group');
            const err = group?.querySelector('.pet-portrait-form__error');
            if (err) err.hidden = true;
          }
        });

        this.wizard?.addEventListener('input', (e) => {
          const input = e.target;
          if (input.type === 'text' || input.tagName === 'TEXTAREA') {
            const wrap = input.closest('.pet-portrait-form__field');
            const err = wrap?.querySelector('.pet-portrait-form__error');
            if (err && input.value.trim()) err.hidden = true;
          }
        });
      }

      nextStep() {
        if (!this.validateCurrentStep()) {
          this.focusFirstErrorInStep(this.currentStep);
          return;
        }

        if (this.currentStep < this.stepCount - 1) {
          if (this.wizard) this.wizard.dataset.direction = 'forward';
          this.currentStep++;
          this.syncStepVisibility();
        }
      }

      prevStep() {
        if (this.currentStep > 0) {
          if (this.wizard) this.wizard.dataset.direction = 'backward';
          this.currentStep--;
          this.syncStepVisibility();
        }
      }

      goToStep(idx) {
        if (idx >= 0 && idx < this.stepCount) {
          this.currentStep = idx;
          this.syncStepVisibility();
        }
      }

      syncStepVisibility() {
        this.steps.forEach((step, i) => {
          step.classList.toggle('is-active', i === this.currentStep);
        });

        const isLastStep = this.currentStep === this.stepCount - 1;
        const activeStep = this.steps[this.currentStep];
        const nextBtn = activeStep?.querySelector('[data-wizard-next]');
        const submitBtn = this.form?.querySelector('.product-form__submit');
        const submitWrap = this.form?.querySelector('.product-form__buttons');

        if (!this._submitOriginalParent && submitWrap) {
          this._submitOriginalParent = submitWrap.parentNode;
          this._submitOriginalNext = submitWrap.nextSibling;
        }

        if (isLastStep && submitWrap && submitBtn) {
          if (nextBtn) nextBtn.style.display = 'none';
          const nav = activeStep.querySelector('.pet-portrait-wizard__nav');
          if (nav) nav.appendChild(submitWrap);
          submitBtn.style.display = '';
        } else {
          if (nextBtn) nextBtn.style.display = '';
          if (submitWrap && this._submitOriginalParent) {
            this._submitOriginalParent.insertBefore(submitWrap, this._submitOriginalNext || null);
          }
          if (submitBtn) submitBtn.style.display = 'none';
        }

        if (this.stickyBar && !isLastStep) {
          this.stickyBar.classList.remove('is-visible');
        }

        const belowForm = this.querySelector('.pet-portrait-form__below-form');
        if (belowForm) {
          belowForm.classList.toggle(
            'pet-portrait-form__below-form--hidden',
            !isLastStep
          );
        }

        this.updateStepper();
        this.updateOrderSummary();

        const counter = this.querySelector('[data-step-counter]');
        if (counter) {
          counter.textContent = `ステップ ${this.currentStep + 1} / ${this.stepCount}`;
        }

        this.trackStepView();
        this.wizard?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      /* ── Per-step validation (3-step flow: Size → Photo → Name/Request) ── */

      validateCurrentStep() {
        this.clearErrors();

        switch (this.currentStep) {
          case 0: {
            const okSize = this.validateSize();
            const okFinish = this.validateFinish();
            return okSize && okFinish;
          }
          case 1:
            return this.validateUpload();
          case 2:
            return this.validatePetName();
          default:
            return true;
        }
      }

      validateOptionGroup(group, errorKey) {
        const variantSelects = this.form.querySelector('variant-selects');
        if (!variantSelects) return true;

        const fieldset = variantSelects.querySelector(`[data-option-group="${group}"]`);
        if (!fieldset) return true;

        const radios = fieldset.querySelectorAll('input[type="radio"]');
        if (!radios.length) return true;

        if (!Array.from(radios).some((r) => r.checked)) {
          this.showError(errorKey);
          return false;
        }
        return true;
      }

      validateSize() {
        return this.validateOptionGroup('size', 'size');
      }

      validateFinish() {
        return this.validateOptionGroup('finish', 'finish');
      }

      validateUpload() {
        const step = this.steps[1];
        if (!step) return true;

        const appBlock = step.querySelector('.pet-portrait-form__app-block');
        if (!appBlock) return true;

        if (this.hasUploadedFile(appBlock)) {
          // Gate fallback: if the MutationObserver missed the upload (common on
          // mobile, where a file pick fires `change` but no observable DOM
          // mutation), record it now that we know a file is present.
          this.maybeTrackPhotoUpload();
          return true;
        }

        this.showError('upload');
        return false;
      }

      /* Detect an uploaded photo inside the Uploadly app block WITHOUT touching
         Uploadly's own logic. Prefer strong signals (real file input or a
         populated value/URL field); fall back to explicit state markers, then a
         remote image as a last resort (inline SVG/data-uri icons are ignored). */
      hasUploadedFile(appBlock) {
        if (!appBlock) return false;

        const fields = appBlock.querySelectorAll('input, textarea');
        for (const field of fields) {
          if (field.type === 'file' && field.files && field.files.length > 0) {
            return true;
          }
          if (
            field.type !== 'file' &&
            typeof field.value === 'string' &&
            field.value.trim()
          ) {
            if (field.type === 'hidden') return true;
            if (
              /https?:\/\//i.test(field.value) ||
              /\.(jpe?g|png|webp|gif|heic|heif)(\?|$)/i.test(field.value)
            ) {
              return true;
            }
          }
        }

        if (
          appBlock.querySelector(
            '[data-uploaded], [data-file], [class*="uploaded"], [class*="file-name"], [class*="preview"], [class*="success"]'
          )
        ) {
          return true;
        }

        if (
          appBlock.querySelector('img[src^="http"], img[src^="//"], img[src^="/uploads"]')
        ) {
          return true;
        }

        return false;
      }

      validatePetName() {
        if (!this.petNameToggle?.checked) return true;

        const petName = this.form.querySelector('#pet-name');
        if (!petName || !petName.value.trim()) {
          this.showError('pet-name');
          return false;
        }

        return true;
      }

      validateFinal(evt) {
        this.trackEvent('pnh_add_to_cart_attempt', {
          size: this.getSelectedSize(),
          finish: this.getSelectedFinish(),
          has_pet_name: !!this.petNameToggle?.checked
        });

        this.clearErrors();
        let hasError = false;

        if (!this.validateSize()) hasError = true;
        if (!this.validateFinish()) hasError = true;
        if (!this.validateUpload()) hasError = true;
        if (!this.validatePetName()) hasError = true;

        if (hasError) {
          evt.preventDefault();
          evt.stopImmediatePropagation();

          for (let i = 0; i < this.stepCount; i++) {
            const stepErrors = this.steps[i]?.querySelectorAll(
              '.pet-portrait-form__error:not([hidden])'
            );
            if (stepErrors?.length) {
              this.goToStep(i);
              this.focusFirstErrorInStep(i);
              break;
            }
          }
        }
      }

      focusFirstErrorInStep(idx) {
        const step = this.steps[idx];
        if (!step) return;
        const err = step.querySelector('.pet-portrait-form__error:not([hidden])');
        if (!err) return;

        const key = err.getAttribute('data-error-for');
        let control = null;
        if (key === 'size' || key === 'finish') {
          control = step.querySelector(
            `[data-option-group="${key}"] input[type="radio"]`
          );
        } else if (key === 'upload') {
          const appBlock = step.querySelector('.pet-portrait-form__app-block');
          control =
            appBlock?.querySelector('input, button, [tabindex], a') ||
            step.querySelector('[data-guide-open]');
        } else if (key === 'pet-name') {
          control = this.form.querySelector('#pet-name');
        }

        const target = control || err;
        if (target === err && !err.hasAttribute('tabindex')) {
          err.setAttribute('tabindex', '-1');
        }
        if (typeof target.focus === 'function') {
          try {
            target.focus({ preventScroll: false });
          } catch (e) {
            target.focus();
          }
        }
      }

      togglePetName() {
        const show = this.petNameToggle.checked;
        this.petNameFields.hidden = !show;

        if (show) {
          this.trackEvent('pnh_name_enabled', {
            step_number: 3,
            step_name: 'customize',
            has_pet_name: true
          });
        }

        if (!show) {
          const nameInput = this.petNameFields.querySelector('#pet-name');
          if (nameInput) nameInput.value = '';
        }

        this.updateStepper();
      }

      showError(field) {
        const el = this.querySelector(`[data-error-for="${field}"]`);
        if (el) el.hidden = false;
        this.trackEvent('pnh_validation_error', { error_type: field });
      }

      clearErrors() {
        this.querySelectorAll('.pet-portrait-form__error').forEach((el) => {
          el.hidden = true;
        });
      }

      /* ── Step indicator ── */

      initStepper() {
        this.stepper = this.querySelector('.pet-portrait-form__stepper');
        if (!this.stepper) return;
        this.stepItems = Array.from(
          this.stepper.querySelectorAll('[data-stepper-step]')
        );

        this.stepItems.forEach((item, i) => {
          item.addEventListener('click', () => {
            if (i < this.currentStep) this.goToStep(i);
          });
          item.addEventListener('keydown', (e) => {
            const isActivate =
              e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar';
            if (isActivate && i < this.currentStep) {
              e.preventDefault();
              this.goToStep(i);
            }
          });
        });

        this.updateStepper();
      }

      updateStepper() {
        if (!this.stepItems) return;

        this.stepItems.forEach((item, i) => {
          const completed = i < this.currentStep;
          item.classList.toggle('is-complete', completed);
          item.classList.toggle('is-active', i === this.currentStep);
          item.classList.toggle('is-clickable', completed);

          if (completed) {
            item.setAttribute('role', 'button');
            item.setAttribute('tabindex', '0');
            item.removeAttribute('aria-disabled');
          } else {
            item.removeAttribute('role');
            item.setAttribute('tabindex', '-1');
            item.setAttribute('aria-disabled', 'true');
          }

          if (i === this.currentStep) {
            item.setAttribute('aria-current', 'step');
          } else {
            item.removeAttribute('aria-current');
          }
        });
      }

      /* ── Per-size absolute price, synced to the selected finish ── */

      parsePriceMap(str) {
        if (!str) return null;
        const map = {};
        str.split(';').forEach((pair) => {
          const idx = pair.indexOf('=');
          if (idx > -1) {
            const key = pair.slice(0, idx).trim();
            const val = pair.slice(idx + 1).trim();
            if (key) map[key] = val;
          }
        });
        return Object.keys(map).length ? map : null;
      }

      updateSizePrices() {
        const els = this.form?.querySelectorAll('[data-size-price]');
        if (!els || !els.length) return;
        const finish = this.getSelectedOptionValue('finish');
        els.forEach((el) => {
          const map = this.parsePriceMap(el.dataset.sizePrices);
          if (!map) return;
          let price = finish && map[finish] ? map[finish] : null;
          if (!price) {
            const vals = Object.values(map);
            price = vals.length ? vals[0] : '';
          }
          if (price) el.textContent = price;
        });
      }

      /* ── Special requests character counter ── */

      initCharCounter() {
        this.instructionsField = this.form?.querySelector('#special-instructions');
        this.charCounter = this.querySelector('[data-char-counter]');
        if (!this.instructionsField || !this.charCounter) return;
        const max = this.instructionsField.getAttribute('maxlength') || '500';
        const update = () => {
          this.charCounter.textContent = `${this.instructionsField.value.length} / ${max}`;
        };
        this.instructionsField.addEventListener('input', update);
        update();
      }

      /* ── Live order summary (non-input; never affects cart payload) ── */

      initOrderSummary() {
        this.orderSummary = this.querySelector('[data-order-summary]');
        if (!this.orderSummary) return;
        const update = () => this.updateOrderSummary();
        this.form.addEventListener('change', update);
        this.form.addEventListener('input', update);
        this.updateOrderSummary();
      }

      updateOrderSummary() {
        if (!this.orderSummary) return;

        const setText = (sel, val, fallback) => {
          const el = this.orderSummary.querySelector(sel);
          if (el) el.textContent = val || fallback || '—';
        };

        const size = this.getSelectedOptionValue('size');
        const finish = this.getSelectedOptionValue('finish');
        const bgRadio = this.form.querySelector('[data-bg-color]:checked');
        const bg = bgRadio ? bgRadio.value : '';
        const nameOn = !!this.petNameToggle?.checked;
        const nameVal = nameOn
          ? (this.form.querySelector('#pet-name')?.value || '').trim()
          : '';
        const total = this.getFormattedPrice();

        setText('[data-summary-size]', size, '—');
        setText('[data-summary-finish]', finish, '—');

        const bgRow = this.orderSummary.querySelector('[data-summary-bg-row]');
        if (bgRow) bgRow.hidden = !bg;
        setText('[data-summary-bg]', bg, '—');

        const nameRow = this.orderSummary.querySelector('[data-summary-name-row]');
        if (nameRow) nameRow.hidden = !(nameOn && nameVal);
        setText('[data-summary-name]', nameVal, '—');

        setText('[data-summary-total]', total, '—');
      }

      /* ── Image guide modal ── */

      initGuideModal() {
        this.guideModal = this.querySelector('[data-guide-modal]');
        if (!this.guideModal) return;

        this.querySelector('[data-guide-open]')?.addEventListener('click', (e) => {
          e.preventDefault();
          this.openGuide();
        });

        this.guideModal.querySelectorAll('[data-guide-close]').forEach((el) => {
          el.addEventListener('click', () => this.closeGuide());
        });

        this.guideModal.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') this.closeGuide();
        });
      }

      openGuide() {
        if (!this.guideModal) return;
        this.trackEvent('pnh_photo_guide_open', {
          step_number: 2,
          step_name: 'photo'
        });
        this.guideModal.hidden = false;
        requestAnimationFrame(() => {
          this.guideModal.classList.add('is-open');
          this.guideModal.querySelector('.pet-portrait-form__guide-close')?.focus();
        });
        document.body.style.overflow = 'hidden';
      }

      closeGuide() {
        if (!this.guideModal) return;
        this.guideModal.classList.remove('is-open');
        this.guideModal.addEventListener(
          'transitionend',
          () => {
            this.guideModal.hidden = true;
            document.body.style.overflow = '';
          },
          { once: true }
        );
        this.querySelector('[data-guide-open]')?.focus();
      }

      /* ── Sticky Add to Cart (mobile) ── */

      initStickyCart() {
        const submitBtn = this.form?.querySelector('.product-form__submit');
        this.stickyBar = this.querySelector('.pet-portrait-form__sticky-bar');
        if (!submitBtn || !this.stickyBar) return;

        document.body.appendChild(this.stickyBar);

        this.stickyBarBtn = this.stickyBar.querySelector('button');

        if (this.stickyBarBtn) {
          this.stickyBarBtn.addEventListener('click', (e) => {
            e.preventDefault();
            submitBtn.click();
          });
        }

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            ([entry]) => {
              const onLastStep = this.currentStep === this.stepCount - 1;
              this.stickyBar.classList.toggle(
                'is-visible',
                onLastStep && !entry.isIntersecting
              );
            },
            { threshold: 0 }
          );
          this.observer.observe(submitBtn);
        }
      }

      /* ── Analytics helpers ── */

      trackEvent(eventName, params) {
        if (typeof window.pushPnhEvent === 'function') {
          window.pushPnhEvent(eventName, params);
        }
      }

      getSelectedSize() {
        return this.getSelectedOptionValue('size');
      }

      getSelectedFinish() {
        return this.getSelectedOptionValue('finish');
      }

      getSelectedOptionValue(group) {
        const radio = this.form?.querySelector(
          `variant-selects [data-option-group="${group}"] input[type="radio"]:checked`
        );
        if (!radio) return '';
        return radio.value || '';
      }

      trackStepView() {
        if (this.currentStep === 1) {
          this.trackEvent('pnh_photo_step_viewed', {
            step_number: 2,
            step_name: 'photo'
          });
        }
      }

      bindSizeTracking() {
        // Delegated on the persistent form so it survives variant-selects re-renders.
        this.form?.addEventListener('change', (e) => {
          const radio = e.target;
          if (radio.type !== 'radio') return;
          const group = radio.closest('[data-option-group]')?.dataset.optionGroup;
          if (group === 'size') {
            this.trackEvent('pnh_size_selected', {
              step_number: 1,
              step_name: 'size',
              size: radio.value
            });
          } else if (group === 'finish') {
            this.trackEvent('pnh_finish_selected', {
              step_number: 1,
              step_name: 'finish',
              finish: radio.value
            });
            this.updateSizePrices();
          }
        });
      }

      bindFinishTracking() {
        // Finish change tracking is handled by the delegated listener in bindSizeTracking().
      }

      bindBackgroundTracking() {
        this.form?.addEventListener('change', (e) => {
          const radio = e.target;
          if (radio.type !== 'radio' || !radio.hasAttribute('data-bg-color')) return;
          this.trackEvent('pnh_background_selected', {
            step_number: 3,
            step_name: 'details',
            background: radio.value
          });
        });
      }

      /* ── "サイズ感を見る" → open & scroll the matching collapsible tab ── */

      bindSizeGuideLink() {
        const link = this.querySelector('[data-size-guide-link]');
        if (!link) return;
        link.addEventListener('click', () => {
          const heading = (link.dataset.sizeGuideHeading || '').trim();
          const details = this.findSizeGuideDetails(heading);
          if (!details) return;
          details.open = true;
          const summary = details.querySelector('summary');
          (summary || details).scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (summary) summary.focus({ preventScroll: true });
        });
      }

      findSizeGuideDetails(heading) {
        const accordions = document.querySelectorAll('.product__accordion details');
        if (!heading) return accordions[0] || null;
        for (const details of accordions) {
          const title = details.querySelector('.accordion__title');
          if (title && title.textContent.trim().includes(heading)) return details;
        }
        return null;
      }

      /* ── Live price / submit / sticky bar sync on variant change ── */

      subscribeToVariantChange() {
        if (typeof subscribe !== 'function') return;
        this._variantChangeUnsub = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const data = event?.data;
          if (!data || String(data.sectionId) !== String(this.sectionId)) return;
          this.updatePriceUI(data.variant);
        });
      }

      getFormattedPrice() {
        const priceWrapper = document.getElementById(`price-${this.sectionId}`);
        if (!priceWrapper) return '';
        const priceEl = priceWrapper.querySelector('.price');
        const onSale = priceEl?.classList.contains('price--on-sale');
        const selector = onSale
          ? '.price__sale .price-item--sale'
          : '.price__regular .price-item--regular';
        const node = priceWrapper.querySelector(selector);
        return node ? node.textContent.trim() : '';
      }

      updatePriceUI(variant) {
        const strings = window.variantStrings || {};
        const submitBtnText = this.form?.querySelector('.product-form__submit span');
        const stickyPrice = document.querySelector('[data-sticky-price]');
        const stickyBtn = document.querySelector('[data-sticky-btn]');

        const available = !!(variant && variant.available);
        const priceText = available ? this.getFormattedPrice() : '';

        let label;
        if (!variant) {
          label = strings.unavailable || '';
        } else if (!variant.available) {
          label = strings.soldOut || '';
        } else {
          label = this.submitLabel && priceText
            ? `${this.submitLabel} — ${priceText}`
            : this.submitLabel;
        }

        if (submitBtnText && label) submitBtnText.textContent = label;

        if (stickyPrice) stickyPrice.textContent = priceText;
        if (stickyBtn) {
          stickyBtn.textContent = available
            ? this.submitLabel || strings.addToCart || ''
            : strings.soldOut || '';
        }

        this.updateOrderSummary();
      }

      bindUploadTracking() {
        const step = this.steps[1];
        if (!step) return;
        const appBlock = step.querySelector('.pet-portrait-form__app-block');
        if (!appBlock) return;

        this._photoTracked = false;
        this._uploadAppBlock = appBlock;
        const self = this;

        // Signal 1: DOM mutations — catches desktop drag-drop previews and any
        // markup Uploadly injects when a file is added.
        const uploadObserver = new MutationObserver(function () {
          self.maybeTrackPhotoUpload();
        });
        uploadObserver.observe(appBlock, { childList: true, subtree: true, attributes: true });
        this._uploadObserver = uploadObserver;

        // Signal 2: input/change events — a native file pick (camera/gallery)
        // on iPhone Safari / Android Chrome fires `change` on the file input
        // even when it produces no MutationObserver-visible DOM change, which is
        // the likely reason mobile uploads were never recorded. Capture phase so
        // it works regardless of where inside the app block the input lives.
        this._onUploadInput = function () { self.maybeTrackPhotoUpload(); };
        appBlock.addEventListener('change', this._onUploadInput, true);
        appBlock.addEventListener('input', this._onUploadInput, true);
      }

      maybeTrackPhotoUpload() {
        if (this._photoTracked) return;
        if (!this._uploadAppBlock) return;
        if (!this.hasUploadedFile(this._uploadAppBlock)) return;
        this._photoTracked = true;
        this.trackEvent('pnh_photo_uploaded', {
          step_number: 2,
          step_name: 'photo'
        });
      }

      subscribeToCartSuccess() {
        if (typeof subscribe !== 'function') return;
        const self = this;
        this._cartSuccessUnsub = subscribe(PUB_SUB_EVENTS.cartUpdate, function (data) {
          if (data.source !== 'product-form') return;
          self.trackEvent('pnh_add_to_cart_success', {
            size: self.getSelectedSize(),
            finish: self.getSelectedFinish(),
            has_pet_name: !!self.petNameToggle?.checked
          });
        });
      }

      disconnectedCallback() {
        if (window.Shopify && Shopify.designMode) return;
        this.observer?.disconnect();
        this._uploadObserver?.disconnect();
        if (this._uploadAppBlock && this._onUploadInput) {
          this._uploadAppBlock.removeEventListener('change', this._onUploadInput, true);
          this._uploadAppBlock.removeEventListener('input', this._onUploadInput, true);
        }
        if (typeof this._cartSuccessUnsub === 'function') this._cartSuccessUnsub();
        if (typeof this._variantChangeUnsub === 'function') this._variantChangeUnsub();
        this.stickyBar?.remove();
      }
    }
  );
}
